
import React, { useState, useEffect } from 'react';

const DEFAULT_OBJECTIVES = [
  { label: 'Attirer plus de clients', icon: '🧲' },
  { label: 'Générer plus d\'appels', icon: '📞' },
  { label: 'Obtenir plus de devis', icon: '📋' },
  { label: 'Fidéliser la clientèle', icon: '🤝' },
  { label: 'Valoriser une nouveauté', icon: '✨' },
  { label: 'Augmenter la notoriété', icon: '📣' },
  { label: 'Recruter / embaucher', icon: '👥' },
];

const DEFAULT_ANGLES = [
  { label: 'Conseil expert', icon: '💡' },
  { label: 'Témoignage client', icon: '⭐' },
  { label: 'FAQ / Questions fréquentes', icon: '❓' },
  { label: 'Avant / Après', icon: '🔄' },
  { label: 'Coulisses du métier', icon: '🎬' },
  { label: 'Promotion / Offre', icon: '🏷️' },
  { label: 'Actualité du secteur', icon: '📰' },
  { label: 'Mini-guide pratique', icon: '📖' },
  { label: 'Retour d\'expérience', icon: '💬' },
  { label: 'Présentation équipe', icon: '🏢' },
];

interface ObjectivesData {
  objectives: string[];
  custom_angles: string[];
}

export default function ObjectivesSelector({ value, onChange }: { value: ObjectivesData, onChange: (v: ObjectivesData) => void }) {
  const [objectives, setObjectives] = useState<string[]>(value.objectives || []);
  const [angles, setAngles] = useState<string[]>(value.custom_angles || []);
  const [newAngle, setNewAngle] = useState('');
  const [saved, setSaved] = useState(false);

  // Sync with parent when value changes
  useEffect(() => {
    setObjectives(value.objectives || []);
    setAngles(value.custom_angles || []);
  }, [value]);

  function toggleObjective(obj: string) {
    const updated = objectives.includes(obj) ? objectives.filter(o => o !== obj) : [...objectives, obj];
    setObjectives(updated);
    onChange({ objectives: updated, custom_angles: angles });
    flashSaved();
  }

  function toggleAngle(angle: string) {
    const updated = angles.includes(angle) ? angles.filter(a => a !== angle) : [...angles, angle];
    setAngles(updated);
    onChange({ objectives, custom_angles: updated });
    flashSaved();
  }

  function addCustomAngle() {
    if (newAngle.trim() && !angles.includes(newAngle.trim())) {
      const updated = [...angles, newAngle.trim()];
      setAngles(updated);
      onChange({ objectives, custom_angles: updated });
      setNewAngle('');
      flashSaved();
    }
  }

  function removeCustomAngle(angle: string) {
    const updated = angles.filter(a => a !== angle);
    setAngles(updated);
    onChange({ objectives, custom_angles: updated });
    flashSaved();
  }

  function flashSaved() {
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  const isCustomAngle = (angle: string) => !DEFAULT_ANGLES.some(a => a.label === angle);

  return (
    <div className="space-y-5">
      {/* Objectifs business */}
      <div>
        <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
          🎯 Vos objectifs business
          <span className="text-xs font-normal text-blue-600">(sélectionnez ceux qui vous correspondent)</span>
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {DEFAULT_OBJECTIVES.map(({ label, icon }) => {
            const selected = objectives.includes(label);
            return (
              <button
                key={label}
                type="button"
                onClick={() => toggleObjective(label)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all text-left ${
                  selected
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <span className="text-base">{icon}</span>
                <span>{label}</span>
                {selected && <span className="ml-auto text-white">✓</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Angles de contenu */}
      <div>
        <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
          ✍️ Angles de contenu
          <span className="text-xs font-normal text-blue-600">(types de posts que l'IA utilisera)</span>
        </h4>
        <div className="flex flex-wrap gap-2 mb-3">
          {DEFAULT_ANGLES.map(({ label, icon }) => {
            const selected = angles.includes(label);
            return (
              <button
                key={label}
                type="button"
                onClick={() => toggleAngle(label)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                  selected
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                }`}
              >
                <span>{icon}</span>
                <span>{label}</span>
              </button>
            );
          })}
        </div>

        {/* Custom angles ajoutés */}
        {angles.filter(isCustomAngle).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {angles.filter(isCustomAngle).map(angle => (
              <span key={angle} className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1.5 rounded-full text-xs font-medium">
                🏷️ {angle}
                <button
                  type="button"
                  onClick={() => removeCustomAngle(angle)}
                  className="ml-1 text-purple-500 hover:text-red-500 transition-colors"
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Ajout d'un angle custom */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newAngle}
            onChange={e => setNewAngle(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCustomAngle()}
            placeholder="Ajouter un angle personnalisé..."
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
          />
          <button
            type="button"
            onClick={addCustomAngle}
            disabled={!newAngle.trim()}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            + Ajouter
          </button>
        </div>
      </div>

      {/* Feedback sauvegarde */}
      {saved && (
        <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm animate-pulse">
          ✅ Sauvegardé automatiquement
        </div>
      )}
    </div>
  );
}
